import React from 'react'
import { observer } from 'mobx-react-lite'
import { subDays } from 'date-fns/subDays'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { H3, Icon, Text } from '@mxenabled/mxui'

import {
  type StatusBarData,
  ANALYTICS_EVENTS,
  Drawer,
  StatusBar,
  useBudgetsStore,
  useCategoryStore,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
  useTransactionStore,
  useAccountStore,
  useEvent,
} from '../../common'
import { ManageIncome } from '../../recurringtransactions'
import { formatCurrency } from '../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../core/utils/Localization'

export const Overview = observer(() => {
  const theme = useTheme()

  const { onEvent } = useEvent()
  const { visibleAccounts: accounts } = useAccountStore()
  const { budgets: copy, common: commonCopy, recurring: recurringCopy } = useGlobalCopyStore()
  const { incomeCategories, spendCategories } = useCategoryStore()
  const { detailedBudgets: budgets, totalBudgeted } = useBudgetsStore()
  const { incomeTotal } = useRecurringTransactionsStore()
  const { setFilter } = useTransactionStore()

  const [isIncomeOpen, setIsIncomeOpen] = React.useState(false)

  const [spentTotal, spendingStatus] = React.useMemo(() => {
    const spentTotal = spendCategories.reduce((total, current) => total + current.totalAmount, 0)
    let spendingPercentage = spentTotal / totalBudgeted
    if (spendingPercentage > 1) spendingPercentage = 1
    let color = theme.palette.success.main
    if (spendingPercentage > 0.8 && spendingPercentage < 1) {
      color = theme.palette.warning.main
    } else if (spendingPercentage >= 1) {
      color = theme.palette.error.main
    }

    const spendingStatus = [
      {
        color,
        percentage: `${spendingPercentage}`,
      },
    ] as StatusBarData[]
    return [spentTotal, spendingStatus]
  }, [budgets, spendCategories])

  const [incomePaid, incomeStatus] = React.useMemo(() => {
    const incomePaid = Math.abs(
      incomeCategories.reduce((total, current) => total + current.totalAmount, 0),
    )
    let incomePercentage = incomePaid / incomeTotal
    if (incomePercentage > 1) incomePercentage = 1

    const incomeStatus = [
      {
        color: theme.palette.secondary.main,
        percentage: `${incomePercentage}`,
      },
    ]
    return [incomePaid, incomeStatus]
  }, [incomeCategories, incomeTotal])

  const handleOpenIncome = () => {
    setFilter({
      accounts: accounts.map((account) => account.guid),
      dateRange: {
        start: subDays(new Date(), 90),
        end: new Date(),
      },
    })
    setIsIncomeOpen(true)
    onEvent(ANALYTICS_EVENTS.BUDGETS_CLICK_EDIT_INCOME)
  }

  return (
    <Card elevation={2} sx={{ overflow: 'visible' }}>
      <CardHeader sx={{ pb: 8, pl: 24 }} title={<H3>{copy.overview_title}</H3>} />
      <CardContent sx={{ pt: 12, px: 24 }}>
        <Stack gap={24}>
          <Stack gap={8}>
            <Text bold={true} sx={{ mb: 8 }} variant="body1">
              {copy.spending_title}
            </Text>
            <StatusBar data={spendingStatus} height={16} />
            <Text variant="body2">
              {
                buildDynamicCopy(
                  copy.spending_description,
                  formatCurrency(spentTotal, '0,0'),
                  formatCurrency(totalBudgeted, '0,0'),
                ) as string
              }
            </Text>
          </Stack>

          <Stack gap={8}>
            <Stack alignItems="center" direction="row" gap={4} mb={-4}>
              <Text bold={true} variant="body1">
                {copy.income_title}
              </Text>
              <IconButton
                aria-label={recurringCopy.manage_income}
                onClick={handleOpenIncome}
                sx={{ p: 0 }}
              >
                <Icon color="primary" name="edit" />
              </IconButton>
            </Stack>
            <StatusBar data={incomeStatus} height={16} />
            <Text variant="body2">
              {
                buildDynamicCopy(
                  copy.income_description,
                  formatCurrency(incomePaid, '0,0'),
                  formatCurrency(incomeTotal, '0,0'),
                ) as string
              }
            </Text>
          </Stack>
        </Stack>
      </CardContent>

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={isIncomeOpen}
        onClose={() => setIsIncomeOpen(false)}
        title={recurringCopy.manage_income}
      >
        <ManageIncome forOverview={true} />
      </Drawer>
    </Card>
  )
})
